import { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { classNames, useAppConfig } from "@ct-react/core";
import { useLocaleNavigate, useLocaleUrl } from "@ct-react/locale";
import { CustomerInput, User } from "@shared/graphql";
import { actions, form } from "../../i18n/sharable-defs";
import { useCartCount } from "../../hooks/cart";
import { useLazyLocaleApi } from "../../hooks/locale-api";
import { useTools } from "../../hooks/tools";
import { useForm } from "../../hooks/form";
import { validators } from "../../tools/form";
import { useCheckoutContext } from "./outlet";
import Button from "../../components/common/button";
import "./common.scss";
import "./information.scss";

const transDefs = defineMessages({
  logTitle: { id: "cart.connect.log-title", defaultMessage: "S'identifier" },
  loginHelp: { id: "cart.connect.log-help", defaultMessage: "Je suis déjà inscrit:" },
  registerHelp: { id: "cart.connect.register-help", defaultMessage: "C'est la première fois que je réserve:" },
  continueTitle: { id: "cart.connect.continue-title", defaultMessage: "Continuer sans compte" },
  continueHelp: { id: "cart.connect.continue-help", defaultMessage: "Je souhaite faire une réservation à titre de visiteur et renoncer aux avantages réservés aux titulaires d'un compte en ligne:" }
});

const InformationPage = () => {

  const intl = useIntl();
  const { options: { withLogin } } = useAppConfig();
  const { currentUrl } = useLocaleUrl();
  const navigate = useLocaleNavigate();
  const cartCount = useCartCount();
  const { countries } = useTools();

  const [ loading, setLoading ] = useState<boolean>(true);
  const [ { customer, userId }, dispatch ] = useCheckoutContext();
  const [ display, setDisplay ] = useState<"form" | "connect">("form");
  const [ getUserProfile ] = useLazyLocaleApi<User>("user");
  const { register, watch, reset, formState } = useForm<CustomerInput>();

  useEffect(() => {
    const toConnect = withLogin && !userId && loading && !customer;
    toConnect && setDisplay("connect");
    loading && setLoading(false);
    dispatch({
      type: "change.index",
      payload: {
        index: 2,
        valid: formState.isValid,
        ...(toConnect) && { maskActions: true }
      }
    });
  }, []);

  useEffect(() => {
    if (display !== "form") return;
    const fetchUser = async () => await getUserProfile();
    if (!!customer)
      reset(customer)
    else if (!!userId)
      fetchUser().then(profile => {
        if (!profile) return;
        const { id, username, ...customer } = profile;
        reset(customer as CustomerInput);
      });
  }, [ display, userId ]);

  useEffect(() => {
    cartCount === 0 && navigate("/checkout/", { replace: true });
  }, [ cartCount ]);

  useEffect(() => {
    (!loading && display === "form") && dispatch({ type: "change.index", payload: { index: 2, valid: formState.isValid }});
  }, [ display, formState.isValid ]);

  useEffect(() => {
    if (!formState.isValid) return;
    navigate("/checkout/information", { replace: true, state: { customer: watch() }})
    dispatch({ type: "change.customer.contact", payload: watch() as CustomerInput });
  }, [ formState.isValid, watch() ]);

  if (loading)
    return (<Skeleton className="cart-skeleton" />);

  if (display === "connect")
    return (
      <div className="cart-item-wrap boxed two-col separator">
        <div>
          <h2>{intl.formatMessage(transDefs.logTitle)}</h2>
          <dl className="account-options">
            <dt>{intl.formatMessage(transDefs.loginHelp)}</dt>
            <dd>
              <a className="btn primary" href={`/login?target=${currentUrl}`}>
                {intl.formatMessage(actions.login)}
              </a>
              </dd>
            <dt>{intl.formatMessage(transDefs.registerHelp)}</dt>
            <dd>
              <a className="btn primary" href={`/signup?target=${currentUrl}`}>
                {intl.formatMessage(actions.register)}
              </a>
            </dd>
          </dl>
        </div>
        <div>
          <h2>{intl.formatMessage(transDefs.continueTitle)}</h2>
          <dl className="account-options">
            <dt>{intl.formatMessage(transDefs.continueHelp)}</dt>
            <dd>
              <Button className="primary" onClick={() => setDisplay("form")}>
                {intl.formatMessage(actions.continue)}
              </Button></dd>
          </dl>
        </div>
      </div>);

  return (
    <form className="two-col customer-form">
      <div>
        <div className="f-group">
          <input type="text"
                 className={classNames({ dirty: "firstName" in formState.dirtyFields })}
                 {...register("firstName", {
                   required: intl.formatMessage(form.emptyError)
                 })}
                 placeholder={intl.formatMessage(form.firstName)}
                 autoComplete="given-name" />
          <p className="f-error">
            {!!formState.dirtyFields["firstName"] && formState.dirtyFields["firstName"].error}
          </p>
        </div>
        <div className="f-group">
          <input type="text"
                 className={classNames({ dirty: "lastName" in formState.dirtyFields })}
                 {...register("lastName", {
                   required: intl.formatMessage(form.emptyError)
                 })}
                 placeholder={intl.formatMessage(form.lastName)}
                 autoComplete="family-name" />
          <p className="f-error">
            {!!formState.dirtyFields["lastName"] && formState.dirtyFields["lastName"].error}
          </p>
        </div>
        <div className="f-group">
          <input type="email"
                 className={classNames({ dirty: "email" in formState.dirtyFields })}
                 {...register("email", {
                   required: intl.formatMessage(form.emptyError),
                   validators: { ...validators.emailFormat(intl.formatMessage(form.emptyError) )}
                 })}
                 placeholder={intl.formatMessage(form.email)}
                 autoComplete="email" />
          <p className="f-error">
            {!!formState.dirtyFields["email"] && formState.dirtyFields["email"].error}
          </p>
        </div>
        <div className="f-group">
          <input type="tel"
                 className={classNames({ dirty: "phone" in formState.dirtyFields })}
                 {...register("phone", {
                   required: intl.formatMessage(form.emptyError),
                   validators: { ...validators.phoneFormat(intl.formatMessage(form.phoneError) )}
                 })}
                 placeholder={intl.formatMessage(form.phone)}
                 autoComplete="tel" />
          <p className="f-error">
            {!!formState.dirtyFields["phone"] && formState.dirtyFields["phone"].error}
          </p>
        </div>
      </div>
      <div>
        <div className="f-group">
          <div className="fl-group">
            <input type="text"
                   className={classNames({ dirty: "address.street" in formState.dirtyFields })}
                   {...register("address.street", {
                     required: intl.formatMessage(form.emptyError)
                   })}
                   placeholder={intl.formatMessage(form.street)}
                   autoComplete="street-address" />
            <input type="text"
                   className={classNames("street-min", { dirty: "address.streetNumber" in formState.dirtyFields })}
                   {...register("address.streetNumber")}
                   placeholder={intl.formatMessage(form.streetNumber)} />
          </div>
          <p className="f-error">
            {!!formState.dirtyFields["address.street"] && formState.dirtyFields["address.street"].error}
          </p>
        </div>
        <div className="f-group">
          <input type="text"
                 className={classNames({ dirty: "address.streetComplement" in formState.dirtyFields })}
                 {...register("address.streetComplement")}
                 placeholder={intl.formatMessage(form.streetComplement)} />
          <p className="f-error" />
        </div>
        <div className="f-group">
          <div className="fl-group">
            <input type="text"
                   className={classNames("zip-min", { dirty: "address.zip" in formState.dirtyFields })}
                   {...register("address.zip", {
                     required: intl.formatMessage(form.emptyError)
                   })}
                   placeholder={intl.formatMessage(form.zip)}
                   autoComplete="postal-code" />
            <input type="text"
                   {...register("address.city", {
                     required: intl.formatMessage(form.emptyError)
                   })}
                   placeholder={intl.formatMessage(form.city)} />
          </div>
          <p className="f-error">
            {(!!formState.dirtyFields["address.zip"] || !!formState.dirtyFields["address.city"]) &&
              (formState.dirtyFields["address.zip"] || formState.dirtyFields["address.city"]).error
            }
          </p>
        </div>
        <div className="f-group">
          <select className={classNames({ dirty: "address.country" in formState.dirtyFields })}
                  {...register("address.country", {
                    required: intl.formatMessage(form.emptyError)
                  })}
                  autoComplete="country">
            <option value="">{intl.formatMessage(form.countryPlaceholder)}</option>
            {Object.entries(countries).map(([ value, label ], index) => (
              <option key={index} value={value}>{label}</option>
            ))}
          </select>
          <p className="f-error">
            {!!formState.dirtyFields["address.country"] && formState.dirtyFields["address.country"].error}
          </p>
        </div>
      </div>
    </form>);

}

export default InformationPage;
